import React, { useEffect, useState, createRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { Grid, InputLabel } from '@ui-propulsion/react-components';
import Dropdown from '../../dropdown/dropdown';
import heroPrivacySecurityPd from '../images/heroPrivacySecurityPd.svg';
import CurrencyInput from '../../currencyInput/currencyInput';
import { getNumberFromString } from '../../currencyInput/helpers';
import trackHeapEvent from '../../../services/helpers/trackHeapEvent';
import { capitalizeFirstLetter } from '../../../utils/util';
import styles from './heroHomePrequal.module.scss';
import SmartImage from '../../smartImage/smartImage';

function HeroHomePrequal({
  url = '',
  loanPurposes = null,
  contentNote = '',
  contentEmail = null,
  inputAmountLabel = '',
  inputAmountPlaceHolder = '',
  defaultMaxAmount = null,
  submitBtnText = 'Check Your Rate',
  submitBtnTheme = 'red',
  experimentName,
  experimentVariation,
}) {
  const [docked, setDocked] = useState(false);
  const [inputLoaded, setInputLoaded] = useState(false);
  const [hasCurrencyError, setHasCurrencyError] = useState('');
  const [hasDropdownError, setHasDropdownError] = useState('');
  const input = createRef();
  const dropdown = createRef();
  const maxAmountConst = 40000;
  const [maxAmount, setMaxAmount] = useState(maxAmountConst);
  const [amountLabel, setAmountLabel] = useState(inputAmountLabel);
  const [amountPlaceHolder, setAmountPlaceHolder] = useState(
    inputAmountPlaceHolder
  );

  const setFocusInput = useCallback(() => {
    if (input.current && !inputLoaded) {
      input.current.focus();
    }
    setInputLoaded(true);
  }, [input, inputLoaded]);

  useEffect(() => {
    setFocusInput();
  }, [setFocusInput]);

  const handleSubmit = () => {
    let hasError = false;
    const loanAmount = getNumberFromString(input.current.value);
    const loanPurpose = dropdown.current.value;
    const loanUrl = loanPurposes.find((item) => item.value === loanPurpose);
    const redUrl = loanUrl?.url ?? url;
    if (loanAmount === '' || loanAmount > maxAmount || loanAmount < 1000) {
      setHasCurrencyError(`Enter $1,000 to $${maxAmount.toLocaleString()}`);
      hasError = true;
    } else {
      setHasCurrencyError('');
    }
    if (loanPurpose === '') {
      setHasDropdownError('Select a loan purpose');
      hasError = true;
    } else {
      setHasDropdownError('');
    }
    if (!hasError) {
      const params = [];
      if (loanAmount) {
        params.push(`loanAmount=${loanAmount}`);
      }
      if (loanPurpose) {
        params.push(`loanPurpose=${loanPurpose}`);
      }
      if (experimentName && experimentVariation) {
        params.push(
          `experiment=${experimentName}__${capitalizeFirstLetter(
            experimentVariation
          )}`
        );
      }
      window.location.assign(`${redUrl}?${params.join('&')}`);
    }
  };

  useEffect(() => {
    const isDocked =
      window.document.documentElement.scrollTop >= 0 || window.scrollY >= 0;
    setDocked(isDocked);
  }, []);

  const heroEmailTrack = (analyticsKey) => {
    trackHeapEvent(
      {
        Name: 'Home Page Hero - Respond to Mail Offer',
        CTA: analyticsKey,
      },
      'CMS_Unification'
    );
  };

  useEffect(() => {
    if (defaultMaxAmount) {
      setMaxAmount(defaultMaxAmount);
      setAmountLabel(
        `Personal loans up to $${defaultMaxAmount.toLocaleString()}`
      );
      setAmountPlaceHolder(`Up to $${defaultMaxAmount.toLocaleString()}`);
    }
  }, [defaultMaxAmount]);

  const handlePurposeChange = () => {
    const selectedItem = loanPurposes.find(
      (item) => item.value === dropdown.current.value
    );
    if (!selectedItem || !selectedItem.value) {
      if (!defaultMaxAmount) {
        setMaxAmount(maxAmountConst);
        setAmountLabel(inputAmountLabel);
        setAmountPlaceHolder(inputAmountPlaceHolder);
      } else {
        setMaxAmount(defaultMaxAmount);
        setAmountLabel(
          `Personal loans up to $${defaultMaxAmount.toLocaleString()}`
        );
        setAmountPlaceHolder(`Up to $${defaultMaxAmount.toLocaleString()}`);
      }
    } else {
      const amount =
        selectedItem.customMaxAmount || defaultMaxAmount || maxAmountConst;
      setMaxAmount(amount);
      setAmountLabel(
        selectedItem.customTitle ??
          `Personal loans up to $${amount.toLocaleString()}`
      );
      setAmountPlaceHolder(`Up to $${amount.toLocaleString()}`);
    }
    setHasCurrencyError('');
    setHasDropdownError('');
  };

  return (
    <div
      className={`${styles.heroPrequal} ${docked && styles.heroPrequalDocked}`}
    >
      <div className={styles.heroPrequalText}>{parse(contentNote)}</div>
      <div className={styles.heroPrequalForm}>
        <div className={styles.prequalHorizontalForm}>
          <Grid container spacing={2}>
            <InputLabel required>{amountLabel}</InputLabel>
            <Grid item xs={12} sm={3} className={styles.inputAmountGrid}>
              <CurrencyInput
                className="currencyInput"
                ref={input}
                hasError={hasCurrencyError}
                placeholder={amountPlaceHolder}
              />
            </Grid>
            <Grid item xs={12} sm={5} className={styles.dowpdownGrid}>
              <Dropdown
                ref={dropdown}
                options={loanPurposes}
                errorMessage={hasDropdownError}
                className="heroHomeDropdown"
                elevation
                onChange={handlePurposeChange}
              />
            </Grid>
            <Grid item xs={12} sm={4} className={styles.CtaGrid}>
              <button
                type="submit"
                data-testid="check-rate"
                className={`${styles.submitBtn} ${styles[submitBtnTheme]}`}
                aria-label="submit-rate"
                onClick={handleSubmit}
              >
                {submitBtnText}
              </button>
            </Grid>
          </Grid>
        </div>
        {contentEmail && (
          <div className={styles.heroPrequalActions}>
            <div className={styles.heroPrequalActionsMailLink}>
              <a
                data-testid="email-offer"
                href={contentEmail.link}
                onClick={() => heroEmailTrack(contentEmail.heapEventKey)}
              >
                <span className={styles.contentEmailIcon} role="img" />
                {contentEmail.text}
              </a>
            </div>
          </div>
        )}
      </div>
      <div className={styles.heroPrequalActionsImage}>
        {heroPrivacySecurityPd?.src && (
          <SmartImage
            src={heroPrivacySecurityPd?.src}
            alt="Privacy & Security"
            priority
            height={heroPrivacySecurityPd.height}
            width={heroPrivacySecurityPd.width}
          />
        )}
      </div>
    </div>
  );
}

HeroHomePrequal.propTypes = {
  url: PropTypes.string,
  loanPurposes: PropTypes.arrayOf(PropTypes.object),
  contentNote: PropTypes.string,
  contentEmail: PropTypes.object,
  inputAmountLabel: PropTypes.string,
  inputAmountPlaceHolder: PropTypes.string,
  defaultMaxAmount: PropTypes.number,
  submitBtnText: PropTypes.string,
  submitBtnTheme: PropTypes.string,
};

export default HeroHomePrequal;
